interface PerformanceMetrics {
  frameTime: number;
  fps: number;
  memoryUsage: number;
  gameObjectCount: number;
  enemyCount: number;
  bulletCount: number;
  pickupCount: number;
  weaponCount: number;
  updateTime: number;
  renderTime: number;
  timestamp: number;
}

interface ProfilerConfig {
  enabled: boolean;
  sampleInterval: number; // milliseconds
  maxSamples: number;
  logToConsole: boolean;
  showOverlay: boolean;
  trackMemory: boolean;
  trackGameObjects: boolean;
}

// Extend Performance interface to include memory property
interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

declare global {
  interface Performance {
    memory?: PerformanceMemory;
  }
}

export class PerformanceProfiler {
  private static instance: PerformanceProfiler;
  private config: ProfilerConfig;
  private metrics: PerformanceMetrics[] = [];
  private lastFrameTime: number = 0;
  private frameCount: number = 0;
  private lastSampleTime: number = 0;
  private overlayElement: HTMLElement | null = null;
  private isRunning: boolean = false;
  private updateStartTime: number = 0;
  private renderStartTime: number = 0;
  private updateEndTime: number = 0;
  private renderEndTime: number = 0;
  private lastUpdateTime: number = 0;
  private lastRenderTime: number = 0;

  private constructor() {
    this.config = {
      enabled: this.isDebugModeEnabled(),
      sampleInterval: 1000, // Sample every second
      maxSamples: 300, // Keep 5 minutes of data
      logToConsole: false,
      showOverlay: this.isDebugModeEnabled(),
      trackMemory: true,
      trackGameObjects: true,
    };
  }

  public static getInstance(): PerformanceProfiler {
    if (!PerformanceProfiler.instance) {
      PerformanceProfiler.instance = new PerformanceProfiler();
    }
    return PerformanceProfiler.instance;
  }

  public isDebugModeEnabled(): boolean {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return false;
    }
    return localStorage.getItem("debugMode") === "true";
  }

  public configure(config: Partial<ProfilerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  public start(): void {
    // Only start if debug mode is enabled or explicitly configured to be enabled
    if (!this.isDebugModeEnabled() && !this.config.enabled) {
      console.log(
        "🚫 Performance Profiler disabled (set localStorage.debugMode='true' to enable)"
      );
      return;
    }

    if (this.isRunning) return;

    this.isRunning = true;
    this.lastFrameTime = performance.now();
    this.lastSampleTime = performance.now();
    this.frameCount = 0;

    if (this.config.showOverlay) {
      this.createOverlay();
    }

    console.log("🚀 Performance Profiler started (debug mode enabled)");
  }

  public stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.removeOverlay();
    console.log("⏹️ Performance Profiler stopped");
  }

  public markUpdateStart(): void {
    if (!this.isRunning || !this.isDebugModeEnabled()) return;
    this.updateStartTime = performance.now();
  }

  public markUpdateEnd(): void {
    if (!this.isRunning || !this.isDebugModeEnabled()) return;
    this.updateEndTime = performance.now();
    if (this.updateStartTime > 0) {
      this.lastUpdateTime = this.updateEndTime - this.updateStartTime;
    }
  }

  public markRenderStart(): void {
    if (!this.isRunning || !this.isDebugModeEnabled()) return;
    this.renderStartTime = performance.now();
  }

  public markRenderEnd(): void {
    if (!this.isRunning || !this.isDebugModeEnabled()) return;
    this.renderEndTime = performance.now();
    if (this.renderStartTime > 0) {
      this.lastRenderTime = this.renderEndTime - this.renderStartTime;
    }
  }

  public update(gameCore?: any): void {
    if (!this.isRunning || !this.isDebugModeEnabled()) return;

    const now = performance.now();
    const frameTime = now - this.lastFrameTime;
    this.lastFrameTime = now;
    this.frameCount++;

    // Sample metrics at the configured interval
    if (now - this.lastSampleTime >= this.config.sampleInterval) {
      this.sampleMetrics(gameCore);
      this.lastSampleTime = now;
    }
  }

  private sampleMetrics(gameCore?: any): void {
    const now = performance.now();
    const fps = this.frameCount / (this.config.sampleInterval / 1000);
    this.frameCount = 0;

    const metrics: PerformanceMetrics = {
      frameTime: now - this.lastFrameTime,
      fps: Math.round(fps * 100) / 100,
      memoryUsage: this.getMemoryUsage(),
      gameObjectCount: this.getGameObjectCount(gameCore),
      enemyCount: gameCore?.Enemies?.length || 0,
      bulletCount: gameCore?.BulletGroup?.children?.size || 0,
      pickupCount: gameCore?.PickupGroup?.children?.size || 0,
      weaponCount: gameCore?.Weapons?.length || 0,
      updateTime: this.lastUpdateTime,
      renderTime: this.lastRenderTime,
      timestamp: now,
    };

    this.addMetric(metrics);

    if (this.config.logToConsole) {
      this.logMetrics(metrics);
    }

    if (this.config.showOverlay) {
      this.updateOverlay(metrics);
    }
  }

  private addMetric(metric: PerformanceMetrics): void {
    this.metrics.push(metric);

    // Keep only the last maxSamples
    if (this.metrics.length > this.config.maxSamples) {
      this.metrics.shift();
    }
  }

  private getMemoryUsage(): number {
    if (!this.config.trackMemory) return 0;

    if (performance.memory) {
      return (
        Math.round((performance.memory.usedJSHeapSize / 1024 / 1024) * 100) /
        100
      ); // MB
    }
    return 0;
  }

  private getGameObjectCount(gameCore?: any): number {
    if (!this.config.trackGameObjects || !gameCore) return 0;

    let count = 0;
    if (gameCore.EnemyGroup) count += gameCore.EnemyGroup.children.size;
    if (gameCore.BulletGroup) count += gameCore.BulletGroup.children.size;
    if (gameCore.PickupGroup) count += gameCore.PickupGroup.children.size;
    if (gameCore.WeaponGroup) count += gameCore.WeaponGroup.children.size;

    return count;
  }

  private logMetrics(metrics: PerformanceMetrics): void {
    console.log(`📊 Performance Metrics:
      FPS: ${metrics.fps}
      Frame Time: ${metrics.frameTime.toFixed(2)}ms
      Memory: ${metrics.memoryUsage}MB
      Game Objects: ${metrics.gameObjectCount}
      Enemies: ${metrics.enemyCount}
      Bullets: ${metrics.bulletCount}
      Pickups: ${metrics.pickupCount}
      Weapons: ${metrics.weaponCount}
      Update Time: ${metrics.updateTime.toFixed(2)}ms
      Render Time: ${metrics.renderTime.toFixed(2)}ms
    `);
  }

  private createOverlay(): void {
    if (this.overlayElement) return;

    this.overlayElement = document.createElement("div");
    this.overlayElement.id = "performance-overlay";
    this.overlayElement.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.8);
      color: #00ff00;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      padding: 10px;
      border-radius: 5px;
      z-index: 10000;
      min-width: 200px;
      pointer-events: none;
      line-height: 1.4;
    `;

    document.body.appendChild(this.overlayElement);
  }

  private updateOverlay(metrics: PerformanceMetrics): void {
    if (!this.overlayElement) return;

    const fpsColor =
      metrics.fps >= 25 ? "#00ff00" : metrics.fps >= 15 ? "#ffff00" : "#ff0000";
    const memoryColor =
      metrics.memoryUsage < 100
        ? "#00ff00"
        : metrics.memoryUsage < 200
        ? "#ffff00"
        : "#ff0000";

    this.overlayElement.innerHTML = `
      <div style="color: ${fpsColor}; font-weight: bold;">FPS: ${
      metrics.fps
    }</div>
      <div>Frame: ${metrics.frameTime.toFixed(1)}ms</div>
      <div style="color: ${memoryColor};">Memory: ${metrics.memoryUsage}MB</div>
      <div>Objects: ${metrics.gameObjectCount}</div>
      <div>Enemies: ${metrics.enemyCount}</div>
      <div>Bullets: ${metrics.bulletCount}</div>
      <div>Pickups: ${metrics.pickupCount}</div>
      <div>Weapons: ${metrics.weaponCount}</div>
      <div>Update: ${metrics.updateTime.toFixed(1)}ms</div>
      <div>Render: ${metrics.renderTime.toFixed(1)}ms</div>
    `;
  }

  private removeOverlay(): void {
    if (this.overlayElement) {
      document.body.removeChild(this.overlayElement);
      this.overlayElement = null;
    }
  }

  public getMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  public getAverageMetrics(lastN?: number): Partial<PerformanceMetrics> {
    const samples = lastN ? this.metrics.slice(-lastN) : this.metrics;
    if (samples.length === 0) return {};

    const avg = samples.reduce((acc, metric) => {
      Object.keys(metric).forEach((key) => {
        if (typeof metric[key] === "number" && key !== "timestamp") {
          acc[key] = (acc[key] || 0) + metric[key];
        }
      });
      return acc;
    }, {} as any);

    Object.keys(avg).forEach((key) => {
      avg[key] = Math.round((avg[key] / samples.length) * 100) / 100;
    });

    return avg;
  }

  public getPerformanceReport(): string {
    const avgMetrics = this.getAverageMetrics();
    const recentMetrics = this.getAverageMetrics(10); // Last 10 samples
    const totalSamples = this.metrics.length;

    return `
🎮 TON Survivors Performance Report
=====================================

📊 Overall Averages (${totalSamples} samples):
  • FPS: ${avgMetrics.fps}
  • Frame Time: ${avgMetrics.frameTime}ms
  • Memory Usage: ${avgMetrics.memoryUsage}MB
  • Game Objects: ${avgMetrics.gameObjectCount}
  • Enemies: ${avgMetrics.enemyCount}
  • Bullets: ${avgMetrics.bulletCount}
  • Pickups: ${avgMetrics.pickupCount}
  • Update Time: ${avgMetrics.updateTime}ms
  • Render Time: ${avgMetrics.renderTime}ms

📈 Recent Performance (last 10 samples):
  • FPS: ${recentMetrics.fps}
  • Frame Time: ${recentMetrics.frameTime}ms
  • Memory Usage: ${recentMetrics.memoryUsage}MB
  • Game Objects: ${recentMetrics.gameObjectCount}

🚨 Performance Issues Detected:
${this.analyzePerformanceIssues()}

💡 Recommendations:
${this.getRecommendations()}
    `;
  }

  private analyzePerformanceIssues(): string {
    const issues: string[] = [];
    const avgMetrics = this.getAverageMetrics();

    if (avgMetrics.fps && avgMetrics.fps < 20) {
      issues.push(`  • Low FPS: ${avgMetrics.fps} (target: 30)`);
    }

    if (avgMetrics.frameTime && avgMetrics.frameTime > 33) {
      issues.push(
        `  • High frame time: ${avgMetrics.frameTime}ms (target: <33ms)`
      );
    }

    if (avgMetrics.memoryUsage && avgMetrics.memoryUsage > 150) {
      issues.push(`  • High memory usage: ${avgMetrics.memoryUsage}MB`);
    }

    if (avgMetrics.gameObjectCount && avgMetrics.gameObjectCount > 500) {
      issues.push(`  • Too many game objects: ${avgMetrics.gameObjectCount}`);
    }

    if (avgMetrics.updateTime && avgMetrics.updateTime > 16) {
      issues.push(`  • Slow update loop: ${avgMetrics.updateTime}ms`);
    }

    return issues.length > 0
      ? issues.join("\n")
      : "  • No major issues detected";
  }

  private getRecommendations(): string {
    const latest = this.getLatestMetric();
    if (!latest) return "";

    const recommendations: string[] = [
      "🎯 PERFORMANCE OPTIMIZATION RECOMMENDATIONS:",
      "",
    ];

    // FPS recommendations
    if (latest.fps < 30) {
      recommendations.push(
        "⚠️ CRITICAL: FPS below 30! Consider these optimizations:",
        "  • Reduce enemy count or spawn rate",
        "  • Implement object culling outside camera view",
        "  • Use smaller physics bodies",
        "  • Reduce particle effects",
        ""
      );
    } else if (latest.fps < 50) {
      recommendations.push(
        "⚠️ WARNING: FPS below 50. Optimization suggestions:",
        "  • Optimize update loops (use for instead of forEach)",
        "  • Reduce depth sorting frequency",
        "  • Cache expensive calculations",
        ""
      );
    }

    // Memory recommendations
    if (latest.memoryUsage > 100) {
      recommendations.push(
        "🧠 MEMORY: High memory usage detected:",
        "  • Implement proper object pooling",
        "  • Clean up unused textures and sounds",
        "  • Limit maximum active objects",
        ""
      );
    }

    // Object count recommendations
    if (latest.enemyCount > 50) {
      recommendations.push(
        "👾 ENEMIES: Too many active enemies:",
        "  • Implement enemy despawning when off-screen",
        "  • Use formation-based spawning",
        "  • Reduce enemy spawn rate",
        ""
      );
    }

    if (latest.bulletCount > 100) {
      recommendations.push(
        "🔫 BULLETS: High bullet count detected:",
        "  • Improve bullet pooling",
        "  • Reduce bullet lifetime",
        "  • Implement bullet culling",
        ""
      );
    }

    if (latest.pickupCount > 30) {
      recommendations.push(
        "💎 PICKUPS: Too many active pickups:",
        "  • Auto-collect nearby pickups",
        "  • Implement pickup merging",
        "  • Reduce pickup spawn rate",
        ""
      );
    }

    // Update time recommendations
    if (latest.updateTime > 16) {
      recommendations.push(
        "🔄 UPDATE LOOP: Slow update detected:",
        "  • Optimize game object update methods",
        "  • Use efficient iteration patterns",
        "  • Reduce update frequency for non-critical objects",
        "  • Cache repeated calculations",
        ""
      );
    }

    // Performance optimizations implemented
    recommendations.push(
      "✅ OPTIMIZATIONS IMPLEMENTED:",
      "  • Efficient update loop iterations",
      "  • Reduced containment rect checking frequency",
      "  • Enemy view checking optimization",
      "  • Bullet pooling improvements",
      "  • Physics settings optimization",
      ""
    );

    // Additional general recommendations
    recommendations.push(
      "💡 ADDITIONAL OPTIMIZATIONS:",
      "  • Use texture atlases for sprites",
      "  • Implement level-of-detail for distant objects",
      "  • Use fixed timestep for physics",
      "  • Reduce collision checking frequency",
      "  • Implement spatial partitioning for large maps",
      "  • Use compressed textures (WebP)",
      "  • Minimize DOM manipulation during gameplay",
      ""
    );

    return recommendations.join("\n");
  }

  public exportMetrics(): string {
    return JSON.stringify(this.metrics, null, 2);
  }

  public clearMetrics(): void {
    this.metrics = [];
    console.log("📊 Performance metrics cleared");
  }

  public enableDebugMode(): void {
    // Set localStorage flag
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("debugMode", "true");
    }

    this.configure({
      enabled: true,
      logToConsole: true,
      showOverlay: true,
      trackMemory: true,
      trackGameObjects: true,
    });

    // Restart profiler if not running
    if (!this.isRunning) {
      this.start();
    } else {
      // If already running, just update overlay
      if (this.config.showOverlay && !this.overlayElement) {
        this.createOverlay();
      }
    }

    console.log("🐛 Performance profiler debug mode enabled");
  }

  public disableDebugMode(): void {
    // Remove localStorage flag
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("debugMode");
    }

    this.configure({
      enabled: false,
      logToConsole: false,
      showOverlay: false,
    });

    // Stop profiler and remove overlay
    this.stop();

    console.log("🐛 Performance profiler debug mode disabled");
  }

  public getStatus(): string {
    const debugModeEnabled = this.isDebugModeEnabled();
    return `
🎮 Performance Profiler Status
==============================
• Debug Mode (localStorage): ${debugModeEnabled}
• Running: ${this.isRunning}
• Enabled: ${this.config.enabled}
• Overlay: ${this.config.showOverlay}
• Console Logging: ${this.config.logToConsole}
• Memory Tracking: ${this.config.trackMemory}
• Game Object Tracking: ${this.config.trackGameObjects}
• Sample Interval: ${this.config.sampleInterval}ms
• Max Samples: ${this.config.maxSamples}
• Current Samples: ${this.metrics.length}

${
  !debugModeEnabled
    ? "💡 Tip: Run profiler.enableDebugMode() to start profiling"
    : ""
}
    `;
  }

  private getLatestMetric(): PerformanceMetrics | undefined {
    return this.metrics[this.metrics.length - 1];
  }

  public enableDetailedProfiling(): void {
    if (typeof window === "undefined") return;

    localStorage.setItem("debugMode", "true");
    this.config.enabled = true;
    this.config.showOverlay = true;
    this.config.logToConsole = true;

    console.log("🔍 DETAILED PERFORMANCE PROFILING ENABLED");
    console.log("📊 Performance recommendations:");
    console.log("1. Open browser DevTools → Performance tab");
    console.log("2. Record for 10-15 seconds during gameplay");
    console.log("3. Look for:");
    console.log("   • Long tasks (>50ms)");
    console.log("   • High memory usage");
    console.log("   • Excessive garbage collection");
    console.log("   • Main thread blocking");
    console.log("");
    console.log("🎯 Use this in console to enable:");
    console.log("window.profiler.enableDetailedProfiling()");
    console.log("");
    console.log("🚀 Common fixes:");
    console.log("• Reduce enemy/bullet count");
    console.log("• Disable physics debug graphics");
    console.log("• Use texture atlases");
    console.log("• Implement object pooling");

    this.start();
  }

  public getCurrentBottlenecks(): string {
    if (!this.isRunning || this.metrics.length === 0) {
      return "❌ Profiler not running. Enable with: profiler.start()";
    }

    const latest = this.getLatestMetric();
    if (!latest) return "❌ No metrics available";

    const bottlenecks: string[] = [];

    if (latest.fps < 30) {
      bottlenecks.push("🚨 CRITICAL: FPS extremely low (<30)");
    } else if (latest.fps < 45) {
      bottlenecks.push("⚠️ WARNING: FPS low (<45)");
    }

    if (latest.updateTime > 10) {
      bottlenecks.push(
        `🐌 Slow update loop: ${latest.updateTime.toFixed(2)}ms`
      );
    }

    if (latest.enemyCount > 50) {
      bottlenecks.push(`👾 Too many enemies: ${latest.enemyCount}`);
    }

    if (latest.bulletCount > 100) {
      bottlenecks.push(`🔫 Too many bullets: ${latest.bulletCount}`);
    }

    if (latest.memoryUsage > 100) {
      bottlenecks.push(`🧠 High memory usage: ${latest.memoryUsage}MB`);
    }

    if (bottlenecks.length === 0) {
      return "✅ No obvious bottlenecks detected. Performance looks good!";
    }

    return bottlenecks.join("\n");
  }

  public getOptimizationSuggestions(): string {
    const latest = this.getLatestMetric();
    if (!latest) return "❌ No metrics available";

    const suggestions: string[] = ["🎯 IMMEDIATE ACTIONS:", ""];

    if (latest.fps < 30) {
      suggestions.push(
        "🚨 CRITICAL FPS ISSUES:",
        "1. Reduce enemy spawn rate by 50%",
        "2. Limit bullets on screen to 50",
        "3. Disable particle effects temporarily",
        "4. Use smaller sprite sizes",
        ""
      );
    }

    if (latest.enemyCount > 30) {
      suggestions.push(
        "👾 ENEMY OPTIMIZATION:",
        "• Reduce stage maximum enemies",
        "• Implement better culling",
        "• Use formation spawning less frequently",
        ""
      );
    }

    if (latest.updateTime > 8) {
      suggestions.push(
        "⚡ UPDATE LOOP OPTIMIZATION:",
        "• Cache calculations between frames",
        "• Reduce collision checks",
        "• Skip updates for off-screen objects",
        ""
      );
    }

    suggestions.push(
      "🏆 PROVEN OPTIMIZATIONS:",
      "• Use texture atlases instead of individual images",
      "• Enable object pooling for all entities",
      "• Reduce physics body complexity",
      "• Limit audio processing",
      "• Use efficient data structures",
      ""
    );

    return suggestions.join("\n");
  }
}

// Global instance for easy access
export const profiler = PerformanceProfiler.getInstance();

// Add global console methods for easy debugging
declare global {
  interface Window {
    profiler: PerformanceProfiler;
  }
}

// Make profiler available globally for console access
if (typeof window !== "undefined") {
  window.profiler = profiler;

  // Only show help message if debug mode is enabled
  if (profiler.isDebugModeEnabled()) {
    // Add helpful console methods
    console.log(`
🎮 TON Survivors Performance Profiler
=====================================
Debug mode is enabled! Profiler will start automatically.

Available console commands:
• profiler.getPerformanceReport() - Get detailed performance report
• profiler.exportMetrics() - Export raw metrics as JSON
• profiler.clearMetrics() - Clear all collected metrics
• profiler.getMetrics() - Get all raw metrics
• profiler.getAverageMetrics() - Get average metrics
• profiler.configure({...}) - Configure profiler settings
• profiler.enableDebugMode() - Enable debug mode (overlay + console logging)
• profiler.disableDebugMode() - Disable debug mode
• profiler.getStatus() - Show profiler status and configuration

Example usage:
profiler.enableDebugMode();
profiler.configure({ sampleInterval: 500 });
console.log(profiler.getPerformanceReport());
    `);
  } else {
    // Show minimal message when debug mode is disabled
    console.log(`
🎮 TON Survivors Performance Profiler
=====================================
Profiler is disabled for performance. To enable:
• profiler.enableDebugMode() - Enable debug mode
• localStorage.setItem('debugMode', 'true') then refresh
    `);
  }
}
